import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, Square, Trash2, History, Radio, ScrollText, Forward, Pencil, Replace, User, AlertTriangle, Layers, Bookmark, Archive, PlaySquare } from "lucide-react";
import { formatCreated } from "./jobDates";
import { JobDiagnostics } from "./JobDiagnostics";

export const statusColor = {
  running: "var(--c-emerald)",
  completed: "var(--c-sky)",
  idle: "var(--tg-faint)",
  stopped: "var(--c-amber)",
  paused: "var(--c-violet)",
  queued: "var(--tg-muted)",
  rate_limited: "var(--c-pink)",
  error: "var(--c-rose)",
};

export const JobCard = ({ job: j, accounts, templates = [], onStart, onStop, onPause, onResume, onLogs, onEdit, onRemove, onValidate, onRepickDestination, validating }) => {
  const pct = j.total ? Math.min(100, Math.round((j.copied / j.total) * 100)) : null;
  const findName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? a.label : null;
  };
  const jobTemplates = (j.template_ids || [])
    .map((id) => templates.find((t) => t.id === id))
    .filter(Boolean);
  const ckptId = j.mode === "live" ? j.live_last_id : j.last_id;
  const ckptAt = j.last_copied_at;
  const hasCkpt = !!ckptId && j.status !== "completed";
  const ckptLabel = j.status === "running"
    ? (j.mode === "live" ? "Live at message" : "At message")
    : "Resumes from message";
  const ckptTime = (() => {
    if (!ckptAt) return null;
    const d = new Date(ckptAt);
    return isNaN(d.getTime()) ? null : d.toLocaleString();
  })();
  const srcId = j.source_account_id || j.account_id;
  const destId = j.dest_account_id || j.account_id;
  const srcName = j.source_account_name || findName(srcId);
  const destName = j.dest_account_name || findName(destId);
  const anyMissing = j.source_account_missing || j.dest_account_missing;
  const created = formatCreated(j.created_at);

  return (
    <Card
      className={`p-5 ${j.status === "running" ? "tracing-border" : "bg-[var(--tg-surface)] border-[var(--tg-border)]"}`}
      data-testid={`job-${j.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {j.mode === "live" ? <Radio className="h-4 w-4 text-[var(--c-sky)]" />
            : j.mode === "mass_replace" ? <Replace className="h-4 w-4 text-[var(--c-violet)]" />
            : j.mode === "archive" ? <Archive className="h-4 w-4 text-[var(--c-amber)]" />
            : j.mode === "replay" ? <PlaySquare className="h-4 w-4 text-[var(--c-emerald)]" />
            : <History className="h-4 w-4 text-[var(--c-teal)]" />}
          <div>
            <div className="font-head font-semibold flex items-center gap-2">
              {j.name}
              {j.mode === "mass_replace" ? (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ color: "var(--c-violet)", background: "rgb(var(--c-violet-rgb)/0.1)" }}
                  data-testid={`job-style-${j.id}`}
                >
                  mass replace
                </span>
              ) : j.mode === "archive" ? (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ color: "var(--c-amber)", background: "rgb(var(--c-amber-rgb)/0.1)" }}
                  data-testid={`job-style-${j.id}`}
                >
                  vault capture
                </span>
              ) : j.mode === "replay" ? (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ color: "var(--c-emerald)", background: "rgb(var(--c-emerald-rgb)/0.1)" }}
                  data-testid={`job-style-${j.id}`}
                >
                  vault replay
                </span>
              ) : (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={j.copy_style === "clone"
                    ? { color: "var(--c-teal)", background: "rgb(var(--c-teal-rgb)/0.1)" }
                    : { color: "var(--c-sky)", background: "rgb(var(--c-sky-rgb)/0.1)" }}
                  data-testid={`job-style-${j.id}`}
                >
                  {j.copy_style === "clone" ? "clone" : "branded"}
                </span>
              )}
              {j.forward_channel && (
                <span
                  className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
                  style={{ color: "var(--c-violet)", background: "rgb(var(--c-violet-rgb)/0.1)" }}
                  data-testid={`job-forward-${j.id}`}
                  title={`Forwarded via ${j.forward_channel}`}
                >
                  <Forward className="h-2.5 w-2.5" /> forward
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--tg-faint)] font-mono truncate max-w-md">
              {j.mode === "mass_replace"
                ? `Channel: ${j.source} · ${j.replacements?.length || 0} rule(s)`
                : j.mode === "archive"
                ? `Capturing: ${j.source} → Content Vault`
                : `${j.source} → ${j.destinations?.join(", ")}`}
            </div>
            <div className="text-[11px] mt-1 flex items-center gap-1.5 max-w-md" data-testid={`job-account-${j.id}`}>
              <User className="h-3 w-3 text-[var(--tg-faint)] shrink-0" />
              {anyMissing ? (
                <span className="flex items-center gap-1 text-[var(--c-rose)]" data-testid={`job-account-missing-${j.id}`}>
                  <AlertTriangle className="h-3 w-3" /> Account deleted — edit job to pick a connected account
                </span>
              ) : j.mode === "mass_replace" ? (
                <span className="text-[var(--tg-muted)] truncate">{srcName || "—"}</span>
              ) : srcId === destId ? (
                <span className="text-[var(--tg-muted)] truncate">{srcName || "—"}</span>
              ) : (
                <span className="text-[var(--tg-muted)] truncate">{srcName || "—"} <span className="text-[var(--tg-faint)]">→</span> {destName || "—"}</span>
              )}
            </div>
            <div className="text-[11px] mt-1 text-[var(--tg-faint)]" title={created?.tooltip} data-testid={`job-created-${j.id}`}>
              Created {created ? `${created.date} · ${created.time}` : "—"}
            </div>
            {hasCkpt && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[var(--c-emerald)]" data-testid={`job-checkpoint-${j.id}`}>
                <Bookmark className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {ckptLabel} <span className="font-mono font-semibold">#{ckptId}</span>
                  {ckptTime && <span className="text-[var(--tg-faint)]"> · {ckptTime}</span>}
                </span>
              </div>
            )}
            {jobTemplates.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`job-templates-${j.id}`}>
                <Layers className="h-3 w-3 text-[var(--c-violet)] shrink-0" />
                {jobTemplates.map((t) => (
                  <span
                    key={t.id}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ color: "var(--c-violet)", background: "rgb(var(--c-violet-rgb)/0.1)" }}
                    data-testid={`job-template-${j.id}-${t.id}`}
                    title={`${t.rules?.length || 0} rule(s)`}
                  >
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5"
            style={{ color: statusColor[j.status] || "var(--tg-faint)", background: `${statusColor[j.status] || "var(--tg-faint)"}1a` }}
            data-testid={`job-status-${j.id}`}
          >
            {j.status === "running" && <span className="live-dot h-1.5 w-1.5 rounded-full" style={{ background: statusColor[j.status] }} />}
            {j.status}
          </span>
          {(j.status === "running" || j.status === "queued" || j.status === "rate_limited") && (
            <Button size="sm" variant="outline" onClick={() => onPause(j.id)} className="border-[rgb(var(--c-violet-rgb)/0.40)] text-[var(--c-violet)] hover:bg-[rgb(var(--c-violet-rgb)/0.10)]" data-testid={`pause-job-${j.id}`}>
              <Pause className="h-3.5 w-3.5" />
            </Button>
          )}
          {j.status === "paused" && (
            <Button size="sm" onClick={() => onResume(j.id)} className="bg-[rgb(var(--c-violet-rgb)/0.15)] text-[var(--c-violet)] hover:bg-[rgb(var(--c-violet-rgb)/0.25)] border border-[rgb(var(--c-violet-rgb)/0.30)]" data-testid={`resume-job-${j.id}`}>
              <Play className="h-3.5 w-3.5" />
            </Button>
          )}
          {(j.status === "running" || j.status === "paused" || j.status === "queued" || j.status === "rate_limited") ? (
            <Button size="sm" variant="outline" onClick={() => onStop(j.id)} className="border-[rgb(var(--c-amber-rgb)/0.40)] text-[var(--c-amber)] hover:bg-[rgb(var(--c-amber-rgb)/0.10)]" data-testid={`stop-job-${j.id}`}>
              <Square className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button size="sm" onClick={() => onStart(j.id)} className="bg-[rgb(var(--c-emerald-rgb)/0.15)] text-[var(--c-emerald)] hover:bg-[rgb(var(--c-emerald-rgb)/0.25)] border border-[rgb(var(--c-emerald-rgb)/0.30)]" data-testid={`start-job-${j.id}`}>
              <Play className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onLogs(j.id)} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid={`logs-job-${j.id}`}>
            <ScrollText className="h-3.5 w-3.5" />
          </Button>
          {j.status !== "running" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(j)}
              className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"
              data-testid={`edit-job-${j.id}`}
              title={j.status === "paused" ? "Edit config, then resume to apply" : "Edit job"}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <button onClick={() => onRemove(j.id)} className="text-[var(--tg-faint)] hover:text-[var(--c-rose)] transition-colors" data-testid={`delete-job-${j.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-6">
        <div>
          <span className="font-head font-bold text-2xl tabular-nums">{j.copied}</span>
          <span className="text-[var(--tg-faint)] text-sm ml-2">
            {j.mode === "mass_replace" ? "updated" : j.mode === "archive" ? "captured" : "copied"}
          </span>
          {j.mode === "mass_replace"
            ? <span className="text-[var(--tg-faint)] text-sm"> / {j.processed} scanned</span>
            : (j.total ? <span className="text-[var(--tg-faint)] text-sm"> / {j.total}</span> : null)}
        </div>
        {j.mode === "replay" && j.replay_delay != null && (
          <div className="text-sm text-[var(--tg-faint)]" data-testid={`job-replay-delay-${j.id}`}>
            <span className="font-head font-bold text-lg tabular-nums text-[var(--c-emerald)]">{j.replay_delay}s</span> between posts
          </div>
        )}
        {j.mode === "live" && j.captured > 0 && (
          <div className="text-sm text-[var(--tg-faint)]">
            <span className="font-head font-bold text-lg tabular-nums text-[var(--c-amber)]">{j.captured}</span> archived
          </div>
        )}
        {pct !== null && (
          <div className="flex-1 max-w-md">
            <Progress value={pct} className="h-1.5 bg-[var(--tg-elevated)]" />
          </div>
        )}
      </div>
      <JobDiagnostics
        job={j}
        onValidate={onValidate}
        onRepickDestination={onRepickDestination}
        validating={validating}
      />
    </Card>
  );
};
